import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Divider,
  Stack,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { Controller } from 'react-hook-form';
import { generateClient } from 'aws-amplify/api';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Schema } from '../../../../../../../../amplify/data/resource';
import { useAuth } from '../../../../../../../utils/context/useAuthContext';
import { extractCompanyId, extractFundNumber, formatMoney } from '../../../../../../../utils/format/format';
import CoinIcon from '../../../../../../../components/icons/CoinIcon';

import TextField from '../../../../../../../components/custom/TextField';
import SendButton from '../../../../../../../components/custom/SendButton';
import { Switch } from '../../../../../../../components/custom/Switch';
import Button from '../../../../../../../components/custom/Button';

interface GenerateFileProps {
  fileId: string;
  amount: number;
  employees: number;
  deposits: number;
  depositsArray: any;
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  bankDetails: string;
  singleReference: string;
  multipleReferences: { [key: string]: string };
  paymentValueDate: string;
}

const apiClient = generateClient<Schema>();

// Helper function to download file from response
const downloadFileFromResponse = (response: any, defaultFileName: string) => {
  try {
    let fileData, fileName, mimeType;
    
    console.log('Raw response received:', response);
    
    // Handle different response formats
    if (response?.data) {
      // If response.data is a string, try to parse it
      if (typeof response.data === 'string') {
        try {
          const parsedData = JSON.parse(response.data);
          console.log('Parsed response data:', parsedData);
          
          if (parsedData.body) {
            // Lambda response structure: {statusCode, headers, body, isBase64Encoded}
            fileData = parsedData.body; // Already base64 from lambda
            
            // Extract filename from Content-Disposition header
            if (parsedData.headers && parsedData.headers['Content-Disposition']) {
              const contentDisposition = parsedData.headers['Content-Disposition'];
              console.log('Content-Disposition header:', contentDisposition);
              
              // Parse filename from Content-Disposition: attachment; filename="filename.ext"
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (filenameMatch && filenameMatch[1]) {
                fileName = filenameMatch[1].replace(/['"]/g, '');
                console.log('Extracted filename from Content-Disposition:', fileName);
              }
            }
            
            // Get content type from headers
            mimeType = parsedData.headers?.['Content-Type'] || 'application/octet-stream';
          }
        } catch (parseError) {
          console.error('Error parsing response data:', parseError);
          return;
        }
      } else if (response.data.body) {
        // Direct access to body (fallback for other response formats)
        fileData = response.data.body;
        fileName = response.data.fileName || response.data.filename || response.data.name;
        mimeType = response.data.mimeType || response.data.contentType || response.data.type || 'application/octet-stream';
      }
    }

    if (!fileData) {
      console.error('No file data found in response');
      return;
    }

    // Use server filename if available, otherwise use default
    const finalFileName = fileName || defaultFileName;
    console.log('Final filename to be used:', finalFileName);

    // Convert base64 string to bytes
    const byteCharacters = atob(fileData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create blob and download
    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`File downloaded successfully: ${finalFileName}`);
    
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

const GenerateFile: React.FC<GenerateFileProps> = ({
  fileId,
  amount,
  deposits,
  depositsArray,
  employees,
  open,
  onClose,
}) => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({});

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [isMultipleReferences, setIsMultipleReferences] = useState(false);
  const [previousGUIDS, setPreviousGUIDS] = useState(false);
  const [previousMoneyGUIDS, setPreviousMoneyGUIDS] = useState(false);

  // Function to parse bank details in format "bank-branch-account"
  const parseBankDetails = (bankDetails: string) => {
    const parts = bankDetails.split('-');
    if (parts.length === 3) {
      return {
        bank: parts[0].trim(),
        branch: parts[1].trim(),
        account: parts[2].trim()
      };
    }
    return { bank: '', branch: '', account: '' };
  };

  const { user } = useAuth();

  const userId = user?.userId;

  const { amount6, amountPiela } = useMemo(() => {
    let sum6 = 0;
    let sumPiela = 0;
    if (Array.isArray(depositsArray)) {
      depositsArray.forEach((deposit) => {
        const depositAmount = parseFloat(deposit?.totalDepositAmount) || 0;
        if (deposit?.paymentMethodCode === '6') {
          sum6 += depositAmount;
        } else {
          sumPiela += depositAmount;
        }
      });
    }
    return { amount6: sum6, amountPiela: sumPiela };
  }, [depositsArray]);

  // Determine scenario and if bank form should be shown
  const scenario = useMemo(() => {
    if (amount6 > 0 && amountPiela > 0) return 'mixed'; // Scenario 2
    if (amount6 > 0 && amountPiela === 0) return 'only6'; // Scenario 3
    if (amount6 === 0 && amountPiela > 0) return 'onlyPiela'; // Scenario 1
    return 'none'; // Default or empty case
  }, [amount6, amountPiela]);

  const showBankForm = scenario === 'mixed' || scenario === 'onlyPiela'; // Show form for scenarios 1 and 2

  useEffect(() => {
    const fetchUser = async () => {
      if (userId) {
        try {
          const fetchUserResponse = await apiClient.models.User.get(
            { id: userId },
            { selectionSet: ['paymentInfo.*'] },
          );
          if (fetchUserResponse.data?.paymentInfo) {
            const { bank, branch, account } = fetchUserResponse.data.paymentInfo;
            if (bank && branch && account) {
              setValue('bankDetails', `${bank}-${branch}-${account}`);
            }
          }
        } catch (error) {
          console.error('Failed to fetch user payment info:', error);
        }
      }
    };
    if (open && showBankForm) {
      // Fetch only if dialog is open and form is needed
      fetchUser();
    }
  }, [userId, open, showBankForm, setValue]);

  const onSubmit = async (formData: FormValues) => {
    try {
      setLoading(true);
      if (!userId) {
        throw new Error('User ID is missing');
      }

      let bank = '', branch = '', account = '';
      
      // Parse bank details if the form was shown
      if (showBankForm && formData.bankDetails) {
        const parsedDetails = parseBankDetails(formData.bankDetails);
        bank = parsedDetails.bank;
        branch = parsedDetails.branch;
        account = parsedDetails.account;
        
        // Update user's payment info
        const bankInfoResponse = await apiClient.models.User.update({
          id: userId,
          paymentInfo: { bank, branch, account },
        });
        // Optional: Check bankInfoResponse for errors
      }

      // Prepare reference numbers based on mode
      let referenceNumbers;
      if (isMultipleReferences) {
        // Multiple references mode - create array with fund numbers and reference numbers
        referenceNumbers = depositsArray.map((deposit: any, index: number) => ({
          fundNumber: deposit?.fundIdentifier,
          referenceNumber: formData.multipleReferences[deposit.id || index] || ''
        }));
      } else {
        // Single reference mode - create array with one entry with fundNumber 0
        referenceNumbers = [{
          fundNumber: 0,
          referenceNumber: formData.singleReference || ''
        }];
      }

      // Call mislakaReport API
      const mislakaResponse = await apiClient.queries.mislakaReport({
        fileId: fileId,
        bank: showBankForm ? bank : undefined,
        branch: showBankForm ? branch : undefined,
        account: showBankForm ? account : undefined,
        referenceNumbers: JSON.stringify(referenceNumbers),
        paymentValueDate: formData.paymentValueDate,
        previousGUIDS: previousGUIDS,
        previousMoneyGUIDS: previousMoneyGUIDS
      });

      // Check response status
      if (mislakaResponse?.data) {
        let parsedResponse;
        try {
          parsedResponse = typeof mislakaResponse.data === 'string' 
            ? JSON.parse(mislakaResponse.data) 
            : mislakaResponse.data;
        } catch (parseError) {
          throw new Error('Failed to parse response from server');
        }

        if (parsedResponse.statusCode && parsedResponse.statusCode !== 200) {
          // Extract error message
          let errorMessage = 'Unknown error occurred';
          if (parsedResponse.body) {
            try {
              const errorBody = typeof parsedResponse.body === 'string' 
                ? JSON.parse(parsedResponse.body) 
                : parsedResponse.body;
              errorMessage = errorBody.message || errorMessage;
            } catch {
              errorMessage = parsedResponse.body;
            }
          }
          throw new Error(errorMessage);
        }

        // Download file if response is successful
        downloadFileFromResponse(mislakaResponse, `mishlakaReport_${fileId}.txt`);
      }

      setLoading(false);
      enqueueSnackbar('File processed and sent via SFTP successfully', {
        variant: 'success',
      });

    } catch (error) {
      console.error('Failed to submit:', error);
      setLoading(false);
      enqueueSnackbar('Failed to process file. Please check your data and try again.', { 
        variant: 'error' 
      });
    }
  };

  const onDownload = async (formData: FormValues) => {
    try {
      setLoading(true);
      if (!userId) {
        throw new Error('User ID is missing');
      }

      let bank = '', branch = '', account = '';
      
      // Parse bank details if the form was shown
      if (showBankForm && formData.bankDetails) {
        const parsedDetails = parseBankDetails(formData.bankDetails);
        bank = parsedDetails.bank;
        branch = parsedDetails.branch;
        account = parsedDetails.account;
        
        // Update user's payment info
        const bankInfoResponse = await apiClient.models.User.update({
          id: userId,
          paymentInfo: { bank, branch, account },
        });
        // Optional: Check bankInfoResponse for errors
      }

      // Prepare reference numbers based on mode
      let referenceNumbers;
      if (isMultipleReferences) {
        // Multiple references mode - create array with fund numbers and reference numbers
        referenceNumbers = depositsArray.map((deposit: any, index: number) => ({
          fundNumber: deposit?.fundIdentifier,
          referenceNumber: formData.multipleReferences[deposit.id || index] || ''
        }));
      } else {
        // Single reference mode - create array with one entry with fundNumber 0
        referenceNumbers = [{
          fundNumber: 0,
          referenceNumber: formData.singleReference || ''
        }];
      }

      // Call mislakaReport API with download: true
      const mislakaResponse = await apiClient.queries.mislakaReport({
        fileId: fileId,
        bank: showBankForm ? bank : undefined,
        branch: showBankForm ? branch : undefined,
        account: showBankForm ? account : undefined,
        referenceNumbers: JSON.stringify(referenceNumbers),
        paymentValueDate: formData.paymentValueDate,
        download: true,
        previousGUIDS: previousGUIDS,
        previousMoneyGUIDS: previousMoneyGUIDS
      });

      // Check response status
      if (mislakaResponse?.data) {
        let parsedResponse;
        try {
          parsedResponse = typeof mislakaResponse.data === 'string' 
            ? JSON.parse(mislakaResponse.data) 
            : mislakaResponse.data;
        } catch (parseError) {
          throw new Error('Failed to parse response from server');
        }

        if (parsedResponse.statusCode && parsedResponse.statusCode !== 200) {
          // Extract error message
          let errorMessage = 'Unknown error occurred';
          if (parsedResponse.body) {
            try {
              const errorBody = typeof parsedResponse.body === 'string' 
                ? JSON.parse(parsedResponse.body) 
                : parsedResponse.body;
              errorMessage = errorBody.message || errorMessage;
            } catch {
              errorMessage = parsedResponse.body;
            }
          }
          throw new Error(errorMessage);
        }

        // Download file if response is successful
        downloadFileFromResponse(mislakaResponse, `mishlakaReport_download_${fileId}.txt`);
      }

      setLoading(false);
      enqueueSnackbar('File downloaded successfully', {
        variant: 'success',
      });
      // onClose(); // Close dialog on success - ВРЕМЕННО ЗАКОММЕНТИРОВАНО ДЛЯ ТЕСТИРОВАНИЯ
    } catch (error) {
      console.error('Failed to download:', error);
      setLoading(false);
      enqueueSnackbar('Failed to download file. Please check your data and try again.', { 
        variant: 'error' 
      });
    }
  };

  const renderAmountSection = () => {
    const amountStyle = {
      fontWeight: '500',
      fontSize: '35px',
      fontFamily: 'Montserrat',
      color: '#C1FE40',
    };
    const labelStyle = {
      fontWeight: '700',
      fontSize: '11px',
      fontFamily: 'AlmoniDL',
      color: '#C1FE40',
    };

    switch (scenario) {
      case 'onlyPiela': // Scenario 1: All non-'6'
        return (
          <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Typography sx={amountStyle}>{formatMoney(amountPiela)}</Typography>
            <Typography sx={labelStyle}>סה״כ לתשלום</Typography>
          </Stack>
        );
      case 'mixed': // Scenario 2: Mixed
        return (
          <Stack
            spacing={1}
            sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}
          >
            <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
              <Typography sx={amountStyle}>{formatMoney(amount6)}</Typography>
              <Typography sx={labelStyle}>תשלום בהוראת קבע</Typography>
            </Stack>
            <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
              <Typography sx={amountStyle}>
                {formatMoney(amountPiela)}
              </Typography>
              <Typography dir="rtl" sx={labelStyle}>
                תשלום ב-PielaPay
              </Typography>
            </Stack>
          </Stack>
        );
      case 'only6': // Scenario 3: All '6'
        return (
          <Stack
            spacing={1}
            sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}
          >
            <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
              <Typography sx={amountStyle}>{formatMoney(amount6)}</Typography>
              <Typography sx={labelStyle}>תשלום בהוראת קבע</Typography>
            </Stack>
            <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
              <Typography dir="rtl" sx={labelStyle}>
                ללא תשלום ב-PielaPay {/* Specific text for no PielaPay */}
              </Typography>
            </Stack>
          </Stack>
        );
      default: // Should not happen with valid data, but provide fallback
        return (
          <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Typography sx={amountStyle}>{formatMoney(0)}</Typography>
            <Typography sx={labelStyle}>סהכ הפקדה</Typography>
          </Stack>
        );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        onClose={onClose}
        PaperProps={{
          sx: {
            width: '500px',
            borderRadius: '35px',
          },
        }}
      >
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          alignItems="center"
          spacing={3}
          sx={{
            '& .MuiDialog-paper': {
              padding: '60px',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
            },
            px: '40px',
            pt: '40px',
            pb: '70px',
          }}
        >
          <Stack direction="row" justifyContent="space-between" width="100%">
            <IconButton onClick={onClose}>
              <Icon icon="material-symbols:close-rounded" />
            </IconButton>
            <Stack direction="row" sx={{ alignItems: 'flex-end' }}>
              <Typography
                fontWeight={'700'}
                fontSize={'16px'}
                fontFamily={'AlmoniDL'}
                color={'#2A3447'}
              >
                אישור שליחת
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            width="100%"
          >
            <Stack
              sx={{
                backgroundColor: '#424242',
                flex: 1,
                borderRadius: '30px',
                py: '30px',
                px: '20px',
                justifyContent: 'center',
                alignItems: 'flex-end',
              }}
            >
              <CoinIcon />
              {renderAmountSection()}
            </Stack>
            <Stack
              sx={{
                backgroundColor: '#F2F4F7',
                flex: 1,
                borderRadius: '30px',
                py: '30px',
                px: '20px',
                justifyContent: 'space-between', // Adjusted for better spacing if content grows
                alignItems: 'flex-end',
              }}
            >
              <Stack
                sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}
              >
                <Typography
                  fontWeight={'500'}
                  fontSize={'35px'}
                  fontFamily={'Montserrat'}
                  color={'#424242'}
                >
                  {deposits}
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'16px'}
                  fontFamily={'AlmoniDL'}
                  color={'#424242'}
                >
                  הוראות תשלום
                </Typography>
              </Stack>
              <Stack
                sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}
              >
                <Typography
                  fontWeight={'500'}
                  fontSize={'35px'}
                  fontFamily={'Montserrat'}
                  color={'#424242'}
                >
                  {employees}
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'16px'}
                  fontFamily={'AlmoniDL'}
                  color={'#424242'}
                >
                  עובדים
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* Conditional Rendering for Bank Form */}
          {showBankForm && (
            <>
              <Divider sx={{ width: '100%' }} />
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Controller
                  name="bankDetails"
                  control={control}
                  rules={{ 
                    required: showBankForm ? 'שדה חובה' : false,
                    pattern: {
                      value: /^\d+-\d+-\d+$/,
                      message: 'פורמט חייב להיות: בנק-סניף-חשבון (לדוגמה: 12-345-678901)'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="פרטי בנק (בנק-סניף-חשבון)"
                      placeholder="לדוגמה: 12-345-678901"
                      fullWidth
                      error={!!fieldState.error}
                      value={field.value || ''}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Stack>
            </>
          )}
          {/* End of Conditional Bank Form */}

          {/* Reference Section */}
          <Divider sx={{ width: '100%' }} />
          <Stack spacing={2} sx={{ width: '100%' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isMultipleReferences}
                  onChange={(e) => setIsMultipleReferences(e.target.checked)}
                />
              }
              label={
                <Typography
                  fontWeight="600"
                  fontSize="14px"
                  fontFamily="AlmoniDL"
                  color="#2A3447"
                >
                  {isMultipleReferences ? 'מספרי אסמכתה מרובים' : 'מספר אסמכתה יחיד'}
                </Typography>
              }
              labelPlacement="start"
              sx={{ justifyContent: 'space-between', margin: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={previousGUIDS}
                  onChange={(e) => setPreviousGUIDS(e.target.checked)}
                />
              }
              label={
                <Typography
                  fontWeight="600"
                  fontSize="14px"
                  fontFamily="AlmoniDL"
                  color="#2A3447"
                  dir="rtl"
                >
                  {previousGUIDS ? 'השתמש ב-GUID קודמים' : 'צור GUID חדשים'}
                </Typography>
              }
              labelPlacement="start"
              sx={{ justifyContent: 'space-between', margin: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={previousMoneyGUIDS}
                  onChange={(e) => setPreviousMoneyGUIDS(e.target.checked)}
                />
              }
              label={
                <Typography
                  fontWeight="600"
                  fontSize="14px"
                  fontFamily="AlmoniDL"
                  color="#2A3447"
                  dir="rtl"
                >
                  {previousMoneyGUIDS ? 'השתמש ב-GUID כספים קודמים' : 'צור GUID כספים חדשים'}
                </Typography>
              }
              labelPlacement="start"
              sx={{ justifyContent: 'space-between', margin: 0 }}
            />

            {isMultipleReferences ? (
              <TableContainer component={Paper} sx={{ borderRadius: '12px' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F2F4F7' }}>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'AlmoniDL', fontWeight: '700' }}>
                        מספר אסמכתה
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'AlmoniDL', fontWeight: '700' }}>
                        סכום
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontFamily: 'AlmoniDL', fontWeight: '700' }}>
                        מספר קרן
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(depositsArray) && depositsArray.map((deposit, index) => (
                      <TableRow key={deposit.id || index}>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Controller
                            name={`multipleReferences.${deposit.id || index}`}
                            control={control}
                            rules={{ required: 'מספר אסמכתה חובה' }}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                label="מספר אסמכתה"
                                fullWidth
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                value={field.value || ''}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Typography fontFamily="Montserrat" fontWeight="500">
                            {formatMoney(deposit?.totalDepositAmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Typography fontFamily="Montserrat" fontWeight="500">
                            {extractFundNumber(deposit?.fundIdentifier) === 0
                  ? extractCompanyId(deposit?.fundIdentifier)
                  : extractFundNumber(deposit?.fundIdentifier)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Controller
                name="singleReference"
                control={control}
                rules={{ required: 'מספר אסמכתה חובה' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="מספר אסמכתה"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    value={field.value || ''}
                  />
                )}
              />
            )}
          </Stack>
          {/* End of Reference Section */}

          {/* Payment Value Date Section */}
          <Divider sx={{ width: '100%' }} />
          <Stack spacing={2} sx={{ width: '100%' }}>
            <Controller
              name="paymentValueDate"
              control={control}
              rules={{ required: 'תאריך תוקף תשלום חובה' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="תאריך תוקף תשלום"
                  placeholder="yyyy-mm-dd"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  value={field.value || ''}
                />
              )}
            />
          </Stack>
          {/* End of Payment Value Date Section */}

          <Divider sx={{ width: '100%' }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center" // Align items vertically
            sx={{ width: '100%' }}
          >
            <SendButton
              label="אישור ושליחה"
              loading={loading}
              onClick={handleSubmit(onSubmit)} // Let handleSubmit handle validation
              light={true}
            />
            <Button
              loading={loading}
              onClick={handleSubmit(onDownload)}
            >
              הורדה
            </Button>
            
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
};

export default GenerateFile;
